import type { MachineEtatOptions, Runnable, TransitionOut } from '@/features/domaine/machine-etats'
import type {
  BatchUpdateLaunch,
  BatchUpdateResult,
  ModuleInfo,
} from '@/features/interface/demande/af/steps/frais/batchUpdate/BatchUpdateType.ts'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat.ts'
import { fusionneEtats } from '@/features/interface/machine-etats/tools.ts'
import { ChoixModule, ChoixNature, SaisieFrais } from '@/features/interface/demande/af/steps/frais/batchUpdate/cartes'
import type { NatureFraisAnnexeModele } from '@/features/interface/demande/af/steps/frais/types.ts'

const ETAT_MODULE_SELECTIONNEE = 'ETAT_MODULE_SELECTIONNEE' as const
const transitionSellectionnerModule: Runnable<ModuleInfo[], Pick<BatchUpdateResult, 'module'>> = {
  run: async (options) => {
    if (options.modelIn.length === 1) {
      return {
        out: { module: options.modelIn[0] },
        type: 'automatique',
      }
    }
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixModule, ...options })

    return {
      out: { module: result.value },
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<Pick<BatchUpdateResult, 'module'>>
  },
}

const ETAT_NATURE_FRAIS_SELECTIONNE = 'ETAT_NATURE_FRAIS_SELECTIONNE' as const
const transitionSellectionnerNatureFrais: Runnable<NatureFraisAnnexeModele[], Pick<BatchUpdateResult, 'natureFraisAnnexe'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixNature, ...options })

    return {
      out: { natureFraisAnnexe: result.value },
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<Pick<BatchUpdateResult, 'natureFraisAnnexe'>>
  },
}

const ETAT_FRAIS_SAISI = 'ETAT_FRAIS_SAISI' as const
const transitionSaisirFrais: Runnable<Pick<BatchUpdateResult, 'natureFraisAnnexe' | 'module'>, Pick<BatchUpdateResult, 'montant' | 'quantite'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: SaisieFrais, ...options })

    return {
      out: { montant: result.value.montant, quantite: result.value.quantite },
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<Pick<BatchUpdateResult, 'montant' | 'quantite'>>
  },
}

export const BatchesUpdateConfiguration: MachineEtatOptions<BatchUpdateLaunch, BatchUpdateResult> = {
  transitions: [
    {
      etatDepart: null,
      etatArrive: ETAT_MODULE_SELECTIONNEE,
      runnable: transitionSellectionnerModule,
      queryIn: ({ launchParams }) => {
        return launchParams!.modules
      },
    },
    {
      etatDepart: ETAT_MODULE_SELECTIONNEE,
      etatArrive: ETAT_NATURE_FRAIS_SELECTIONNE,
      runnable: transitionSellectionnerNatureFrais,
      queryIn: ({ launchParams }) => {
        return launchParams!.natures
      },
    },
    {
      etatDepart: ETAT_NATURE_FRAIS_SELECTIONNE,
      etatArrive: ETAT_FRAIS_SAISI,
      runnable: transitionSaisirFrais,
      queryIn: ({ etats }) => {
        return fusionneEtats<BatchUpdateResult>(etats)
      },
    },
  ],
  resultCallback: (options) => fusionneEtats<BatchUpdateResult>(options.etats),
  disposeCallback: () => useMachineEtatDisplay().hide(),
}
